export interface QueuedAttemptFile {
  idempotencyKey: string;
  userId: string;
  attemptId: string;
  questionId: string;
  name: string;
  type: string;
  blob: Blob;
}

const DB_NAME = 'lms-attempt-files';
const STORE = 'files';

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, 1);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE)) {
        const store = db.createObjectStore(STORE, { keyPath: 'idempotencyKey' });
        store.createIndex('byUser', 'userId');
        store.createIndex('byAttempt', ['userId', 'attemptId']);
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

function finish<T>(db: IDBDatabase, tx: IDBTransaction, request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
    tx.oncomplete = () => db.close();
    tx.onerror = () => {
      db.close();
      reject(tx.error);
    };
  });
}

export async function putAttemptFile(file: QueuedAttemptFile): Promise<void> {
  const db = await openDb();
  const tx = db.transaction(STORE, 'readwrite');
  await finish(db, tx, tx.objectStore(STORE).put(file));
}

export async function readAttemptFile(idempotencyKey: string): Promise<QueuedAttemptFile | null> {
  const db = await openDb();
  const tx = db.transaction(STORE, 'readonly');
  const row = await finish(db, tx, tx.objectStore(STORE).get(idempotencyKey));
  return (row as QueuedAttemptFile | undefined) ?? null;
}

export async function deleteAttemptFile(idempotencyKey: string): Promise<void> {
  const db = await openDb();
  const tx = db.transaction(STORE, 'readwrite');
  await finish(db, tx, tx.objectStore(STORE).delete(idempotencyKey));
}

export async function listAttemptFiles(userId: string, attemptId: string): Promise<QueuedAttemptFile[]> {
  const db = await openDb();
  const tx = db.transaction(STORE, 'readonly');
  const rows = await finish(db, tx, tx.objectStore(STORE).index('byAttempt').getAll([userId, attemptId]));
  return rows as QueuedAttemptFile[];
}

export async function clearAttemptFiles(userId: string, attemptId?: string): Promise<void> {
  const db = await openDb();
  const tx = db.transaction(STORE, 'readwrite');
  const store = tx.objectStore(STORE);
  const rows = await new Promise<QueuedAttemptFile[]>((resolve, reject) => {
    const request = store.index('byUser').getAll(userId);
    request.onsuccess = () => resolve(request.result as QueuedAttemptFile[]);
    request.onerror = () => reject(request.error);
  });
  for (const row of rows) {
    if (!attemptId || row.attemptId === attemptId) store.delete(row.idempotencyKey);
  }
  await new Promise<void>((resolve, reject) => {
    tx.oncomplete = () => {
      db.close();
      resolve();
    };
    tx.onerror = () => {
      db.close();
      reject(tx.error);
    };
  });
}
